import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import logger from "../../../utils/logger";
import { APPLY_TO_BUNDLE_RULE, getCouponIneligibilityReason } from "../../../utils/couponEligibilityUtils";
import CartItem from "../../../models/CartItem";
import OrderShippingLine from "../../../models/OrderShippingLine";
import OrderDiscountLine from "../../../models/OrderDiscountLine";
import Coupon from "../../../models/Coupon";
import { toLower } from "lodash";
import { useCallback } from "react";
import { getSalesTax } from "../../../firebase/Ecommerce";
import { setShippingState } from "../auth";

// friends and family is bound to the account rather than the url, so it is resolved here instead of
// at the call sites - several writers race to set the cart coupon on profile hydration and the last
// one to land would otherwise win
const FRIENDS_AND_FAMILY_COUPON_CODE = 'readyfaf';

const initialState = {
	cartItems: [],
	cartSummary: {
		discountLines: [],
		monthlyDiscountLines: [],
		bundleDiscountLines: [],
		shippingLines: [],
		feeLines: [],

		discountTotal: 0.0, // total discounts to be subtracted from order to calculate subtotal
		discountTax: 0.0, // tax to be subtracted from tax total

		monthlyDiscountTotal: 0.0,
		monthlyDiscountTax: 0.0,

		bundleDiscountTotal: 0.0,
		bundleDiscountTax: 0.0,

		shippingTotal: 0.0, // total for shipping method (w/o tax)
		shippingTax: 0.0, // tax for shipping method

		feeTotal: 0.0, // total for all fee line items (w/o tax)
		feeTax: 0.0, // tax for all fee line items

		cartTotal: 0.0, // total of all order line items (w/o tax)
		cartTax: 0.0, // tax for all order line items

		subtotal: 0.0, // subtotal for the order
		tax: 0.0, // total tax for the order
		total: 0.0, // overall order total

		monthlySubtotal: 0.0,
		monthlyTax: 0.0,
		monthlyTotal: 0.0
	},
	coupon: undefined,
	couponApplied: undefined,
	couponCodeApplied: null,
	paymentMethod: undefined,
	shippingMethod: undefined,
	shippingAddress: null,
	billingAddress: null,
	taxRate: 0.0, // Tax in percentage
	taxRateState: null, // the shipping state taxRate was looked up for
	totalQuantity: 0,
	isCartOpen: false,

	isLoading: false,
	error: null
};

const cartReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'ADD_TO_CART':
			return {
				...state,
				cartItems: [...state.cartItems, payload]
			};
		case 'ADD_SINGLE_ITEM_TO_CART':
			return {
				...state,
				cartItems: [payload]
			};
		case 'UPDATE_CART_ITEM':
			return {
				...state,
				cartItems: state.cartItems.map((item) =>
					item.id === payload.id ? payload : item
				)
			};
		case 'UPDATE_ALL_CART_ITEMS':
			return {
				...state,
				cartItems: payload
			};
		case 'REMOVE_CART_ITEM':
			return {
				...state,
				cartItems: state.cartItems.filter((cartItem) => cartItem.id !== payload)
			};
		case 'UPDATE_TOTAL_QUANTITY':
			return {
				...state,
				totalQuantity: payload
			};
		case 'UPDATE_TAX_RATE':
			return {
				...state,
				taxRate: payload.taxRate,
				taxRateState: payload.taxRateState
			};
		case 'OPEN_CART':
			return {
				...state,
				isCartOpen: true
			};
		case 'CLOSE_CART':
			return {
				...state,
				isCartOpen: false
			};
		case 'UPDATE_CART_SUMMARY_AND_TOTAL_QUANTITY':
			return {
				...state,
				cartSummary: payload.cartSummary,
				totalQuantity: payload.totalQuantity
			};
		case 'UPDATE_PAYMENT_METHOD':
			return {
				...state,
				paymentMethod: payload
			};
		case 'UPDATE_SHIPPING_METHOD':
			return {
				...state,
				shippingMethod: payload
			};
		case 'UPDATE_COUPON':
			return {
				...state,
				coupon: payload
			};
		case 'UPDATE_COUPON_APPLIED':
			return {
				...state,
				couponApplied: payload
			};
		case 'UPDATE_COUPON_CODE_APPLIED':
			return {
				...state,
				couponCodeApplied: payload
			};
		case 'UPDATE_SHIPPING_ADDRESS':
			return {
				...state,
				shippingAddress: payload
			};
		case 'UPDATE_BILLING_ADDRESS':
			return {
				...state,
				billingAddress: payload
			};
		case 'RESET_CART':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

const getTotalQuantity = (items) => CartItem.getTotalQuantity(items);

const getCartItemTaxTotal = (item, taxRate, products, variants) => CartItem.getCartItemTaxTotal(item, taxRate, products, variants);

const calculateShippingMethodCost = (shippingMethod, items, taxRate, products, variants) => CartItem.calculateShippingMethodCost(shippingMethod, items, taxRate, products, variants);

const isFoundInCart = (cartState, id) => {
	const { cartItems } = cartState;
	return !!cartItems.find((item) => item.id === id);
};

const isFoundInCartBySku = (cartState, sku) => {
	const { cartItems } = cartState;
	return !!cartItems.find((item) => item?.sku === sku);
};

export const calculateCartSummary = () => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems, cartSummary, coupon, shippingMethod, taxRate } = cartState;
			const orchestrator = { ...state.orchestrator };
			const { products, variants } = orchestrator;

			let monthlyDiscountLines = [];
			let bundleDiscountLines = [];
			let discountLines = [];
			let bundleDiscountAmount = 0;
			let additionalDiscountAmount = 0;
			let hasRecurringAdditionalDiscount = false;
			// console.log('calculateCartSummary: coupon', coupon);
			if (isObjectNotEmpty(coupon) && isStringNotEmpty(coupon.code)) {
				// console.log('coupon', coupon);
				let discountRes = CartItem.getCartDiscount(coupon, cartItems, taxRate ?? 0, products, variants);
				let discountTotal = discountRes?.discountTotal ?? 0;
				let discountTax = discountRes?.discountTax ?? 0;
				let applyToBundle = isNumberGreaterThanOrEqualToZero(cartItems?.[0]?.bundleCount) && cartItems?.[0]?.bundleCount > 1 && isBooleanTrue(coupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget));
				let applyToBundleCount = isBooleanTrue(applyToBundle) ? cartItems?.[0]?.bundleCount : 1;

				additionalDiscountAmount = discountTotal;
				discountLines.push(new OrderDiscountLine({
					code: coupon.code,
					discount: discountTotal * applyToBundleCount,
					discountTax: discountTax * applyToBundleCount,
					discountTotal: (discountTotal + discountTax) * applyToBundleCount,
					recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget))
				}).toDto());
				if (['BUNDLE', 'RECURRING', 'MIXED_RECURRING'].includes(coupon.discountTarget)) {
					monthlyDiscountLines.push(new OrderDiscountLine({
						code: coupon.code,
						discount: discountTotal,
						discountTax: discountTax,
						discountTotal: (discountTotal + discountTax),
						recurring: true
					}).toDto());
					hasRecurringAdditionalDiscount = true;
				}
			}

			for (const cartItem of cartItems) {
				if (isNumberGreaterThanOrEqualToZero(cartItem.bundleCount) && cartItem.bundleCount > 1) {
					let product = products.find(i => i.id === cartItem?.id);
					let productBundleDiscounts = isListNotEmpty(product?.bundleDiscounts) ? [...product?.bundleDiscounts] : [];
					let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === cartItem.bundleCount);
					if (isObjectNotEmpty(bundleDiscount)) {
						let bundleCoupon = await Coupon.getByCode(bundleDiscount?.couponCode);
						// console.log('bundleCoupon', bundleCoupon);
						if (isObjectNotEmpty(bundleCoupon)) {
							let bundleDiscountRes = CartItem.getCartDiscount(bundleCoupon, cartItems, taxRate, products, variants);
							let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
							let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
							discountLines.push(new OrderDiscountLine({
								code: bundleCoupon?.code,
								discount: bundleDiscountTotal,
								discountTax: bundleDiscountTax,
								discountTotal: bundleDiscountTotal + bundleDiscountTax,
								recurring: true
							}).toDto());
							monthlyDiscountLines.push(new OrderDiscountLine({
								code: bundleCoupon?.code,
								discount: bundleDiscountTotal / (cartItem?.bundleCount ?? 1),
								discountTax: bundleDiscountTax / (cartItem?.bundleCount ?? 1),
								discountTotal: (bundleDiscountTotal + bundleDiscountTax) / (cartItem?.bundleCount ?? 1),
								recurring: true
							}).toDto());
							bundleDiscountLines.push(new OrderDiscountLine({
								code: bundleCoupon?.code,
								discount: bundleDiscountTotal,
								discountTax: bundleDiscountTax,
								discountTotal: bundleDiscountTotal + bundleDiscountTax,
								recurring: true
							}).toDto());
							bundleDiscountAmount += (bundleDiscountTotal / (cartItem?.bundleCount ?? 1));
						}
					}
				}
			};

			let shippingLines = [];
			if (isObjectNotEmpty(shippingMethod) && isListNotEmpty(cartItems)) {
				let shippingMethodCostRes = CartItem.calculateShippingMethodCost(shippingMethod, [...cartItems], taxRate, products, variants);

				shippingLines.push(new OrderShippingLine({
					methodTitle: shippingMethod.name,
					methodId: shippingMethod.id,
					taxStatus: shippingMethod.taxStatus,
					subtotal: shippingMethodCostRes.shippingTotal,
					tax: shippingMethodCostRes.shippingTax,
					total: shippingMethodCostRes.shippingTotal + shippingMethodCostRes.shippingTax
				}).toDto());
			}

			let feeLines = [];

			let discountTotal = CartItem.getDiscountLinesTotal(discountLines);
			let discountTax = CartItem.getDiscountLinesTax(discountLines);

			let monthlyDiscountTotal = CartItem.getDiscountLinesTotal(monthlyDiscountLines);
			let monthlyDiscountTax = CartItem.getDiscountLinesTax(monthlyDiscountLines);

			let bundleDiscountTotal = CartItem.getDiscountLinesTotal(bundleDiscountLines);
			let bundleDiscountTax = CartItem.getDiscountLinesTax(bundleDiscountLines);

			let shippingTotal = CartItem.getShippingLinesTotal(shippingLines);
			let shippingTax = CartItem.getShippingLinesTax(shippingLines);

			let feeTotal = CartItem.getFeeLinesTotal(feeLines);
			let feeTax = CartItem.getFeeLinesTax(feeLines);

			let cartTotal = CartItem.getCartItemsTotal(cartItems, products, variants);
			let cartTax = CartItem.getCartItemsTaxTotal(cartItems, taxRate, products, variants);

			let subtotal = cartTotal + shippingTotal + feeTotal - discountTotal;
			let tax = cartTax + shippingTax + feeTax - discountTax;
			let total = subtotal + tax;

			// a renewal bills the whole plan again with every discount that recurs, so it takes the plan's recurring
			// lines rather than one month of each
			let recurringDiscountLines = discountLines.filter(i => isBooleanTrue(i.recurring));
			let recurringDiscountTotal = CartItem.getDiscountLinesTotal(recurringDiscountLines);
			let recurringDiscountTax = CartItem.getDiscountLinesTax(recurringDiscountLines);

			let monthlySubtotal = cartTotal + shippingTotal + feeTotal - recurringDiscountTotal;
			let monthlyTax = cartTax + shippingTax + feeTax - recurringDiscountTax;
			let monthlyTotal = monthlySubtotal + monthlyTax;

			let updatedCartSummary = {
				...cartSummary,

				discountLines: discountLines,
				monthlyDiscountLines: monthlyDiscountLines,
				bundleDiscountLines: bundleDiscountLines,
				shippingLines: shippingLines,
				feeLines: feeLines,

				discountTotal: discountTotal,
				discountTax: discountTax,

				monthlyDiscountTotal: monthlyDiscountTotal,
				monthlyDiscountTax: monthlyDiscountTax,

				bundleDiscountTotal: bundleDiscountTotal,
				bundleDiscountTax: bundleDiscountTax,

				shippingTotal: shippingTotal,
				shippingTax: shippingTax,

				feeTotal: feeTotal,
				feeTax: feeTax,

				cartTotal: cartTotal,
				cartTax: cartTax,
				// the rate these totals were taxed at, so a payment can tell the total it shows from the one it charges
				taxRate: taxRate,

				subtotal: subtotal,
				tax: tax,
				total: total,

				monthlySubtotal: monthlySubtotal,
				monthlyTax: monthlyTax,
				monthlyTotal: monthlyTotal,

				bundleDiscountAmount: bundleDiscountAmount,
				additionalDiscountAmount: additionalDiscountAmount,
				hasRecurringAdditionalDiscount: hasRecurringAdditionalDiscount
			};

			// console.log('updatedCartSummary', updatedCartSummary);

			const updatedTotalQuantity = CartItem.getTotalQuantity(cartItems);

			dispatch({ type: 'UPDATE_CART_SUMMARY_AND_TOTAL_QUANTITY', payload: { cartSummary: { ...updatedCartSummary }, totalQuantity: updatedTotalQuantity } });

			return updatedCartSummary;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateTaxRate = (taxRate, taxRateState = null) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_TAX_RATE', payload: { taxRate, taxRateState } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updatePaymentMethod = (paymentMethod) => {
	return async (dispatch) => {
		try {
			if (isObjectNotEmpty(paymentMethod)) {
				dispatch({ type: 'UPDATE_PAYMENT_METHOD', payload: { ...paymentMethod } });

				return paymentMethod;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		dispatch({ type: 'UPDATE_PAYMENT_METHOD', payload: null });

		return null;
	}
};

export const updateShippingMethod = (shippingMethod) => {
	return async (dispatch) => {
		try {
			if (isObjectNotEmpty(shippingMethod)) {
				dispatch({ type: 'UPDATE_SHIPPING_METHOD', payload: { ...shippingMethod } });

				return shippingMethod;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		dispatch({ type: 'UPDATE_SHIPPING_METHOD', payload: null });

		return null;
	}
};

export const setCouponCodeApplied = (couponCode) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_COUPON_CODE_APPLIED', payload: couponCode });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const setCouponApplied = (coupon) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_COUPON_APPLIED', payload: coupon });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const updateCouponCode = (couponCode, userId = null, email = null) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const isFriendsAndFamily = isBooleanTrue(state.authState?.profile?.isFriendsAndFamily);
			const resolvedCouponCode = isFriendsAndFamily ? FRIENDS_AND_FAMILY_COUPON_CODE : couponCode;

			dispatch({ type: 'UPDATE_COUPON_CODE_APPLIED', payload: resolvedCouponCode });

			// only a flagged profile can redeem the friends and family code - anyone else entering it
			// falls through to the invalid coupon path
			const coupon = isBooleanFalse(isFriendsAndFamily) && toLower(resolvedCouponCode) === FRIENDS_AND_FAMILY_COUPON_CODE ? null : await Coupon.getByCode(resolvedCouponCode);
			// console.log('updateCouponCode', coupon);

			if (isObjectEmpty(coupon)) {
				dispatch({ type: 'UPDATE_COUPON_APPLIED', payload: null });
				dispatch({ type: 'UPDATE_COUPON', payload: null });
				return coupon;
			}

			// judged against the cart as it is when the lookup lands: the cart can change while the lookup is in flight,
			// and the cart re-check skips a code whose lookup has not landed yet
			const latestState = getState();
			const error = getCouponIneligibilityReason({ coupon, cartItems: latestState.cartState?.cartItems, variants: latestState.orchestrator?.variants, userId, email });
			dispatch({ type: 'UPDATE_COUPON_APPLIED', payload: isStringNotEmpty(error) ? { ...coupon, error } : { ...coupon } });
			// a coupon that fails only on the plan stays the cart coupon: it discounts nothing on a plan it does not cover,
			// but still goes with the order, because the code also credits the sale to its affiliate or referrer
			dispatch({ type: 'UPDATE_COUPON', payload: isStringEmpty(error) || error === APPLY_TO_BUNDLE_RULE ? { ...coupon } : null });

			return isStringNotEmpty(error) ? { ...coupon, error } : coupon;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return null;
	}
};

// the attached coupon follows the cart: its rules are re-checked in memory whenever the product, dose or plan
// changes, so a coupon one plan rules out discounts again on a plan it covers - nothing is looked up again
export const recheckCouponForCart = () => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const { cartItems, coupon, couponApplied, couponCodeApplied } = state.cartState;
			// a code whose lookup has not landed yet still has the previous code's document attached; trimmed and
			// lower-cased the way getByCode looks a code up
			if (isObjectEmpty(couponApplied) || toLower((couponApplied.code ?? '').trim()) !== toLower((couponCodeApplied ?? '').trim())) {
				return;
			}

			const { error: previousError, ...couponDocument } = couponApplied;
			const profile = state.authState?.profile;
			const error = getCouponIneligibilityReason({ coupon: couponDocument, cartItems, variants: state.orchestrator?.variants, userId: profile?.userId, email: profile?.email });
			if ((previousError ?? null) !== (error ?? null)) {
				dispatch({ type: 'UPDATE_COUPON_APPLIED', payload: isStringNotEmpty(error) ? { ...couponDocument, error } : couponDocument });
			}

			const isCartCoupon = isStringEmpty(error) || error === APPLY_TO_BUNDLE_RULE;
			if (isCartCoupon !== isObjectNotEmpty(coupon)) {
				dispatch({ type: 'UPDATE_COUPON', payload: isCartCoupon ? { ...couponDocument } : null });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	};
};

export const resetCouponCode = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_COUPON', payload: null });
			dispatch({ type: 'UPDATE_COUPON_APPLIED', payload: null });
			dispatch({ type: 'UPDATE_COUPON_CODE_APPLIED', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

// a payment waits at most this long for a rate it does not hold before charging the held one, as it does when the lookup fails
const TAX_RATE_LOOKUP_WAIT_MS = 5000;

// one lookup per state at a time: the shipping step, the settled-rate effect and a payment can ask for the same state at once
const taxRateLookups = new Map();

// a lookup is shared only as long as a payment would wait on it, so a hung one never holds back the next request for its state
const lookUpTaxRate = (state) => {
	if (taxRateLookups.has(state) === false) {
		const releaseLookup = () => {
			clearTimeout(expiryTimer);
			if (taxRateLookups.get(state) === lookup) {
				taxRateLookups.delete(state);
			}
		};
		const lookup = getSalesTax(state).then((salesTax) => salesTax?.rate ?? 0).finally(releaseLookup);
		const expiryTimer = setTimeout(releaseLookup, TAX_RATE_LOOKUP_WAIT_MS);
		taxRateLookups.set(state, lookup);
	}

	return taxRateLookups.get(state);
};

export const updateShippingAddress = (shippingAddress) => {
	return async (dispatch, getState) => {
		try {
			if (isObjectNotEmpty(shippingAddress) && isStringNotEmpty(shippingAddress?.state)) {
				dispatch({ type: 'UPDATE_SHIPPING_ADDRESS', payload: { ...shippingAddress } });
				dispatch(setShippingState(shippingAddress?.state));

				const taxRate = await lookUpTaxRate(shippingAddress?.state);
				// console.log(salesTax);
				// the shipping step does not wait out a slow lookup, so a newer address can be in place by the time this answers
				if (getState().cartState.shippingAddress?.state === shippingAddress.state) {
					dispatch(updateTaxRate(taxRate, shippingAddress.state));
				}
			} else {
				dispatch({ type: 'UPDATE_SHIPPING_ADDRESS', payload: null });
				dispatch(setShippingState(null));
				dispatch(updateTaxRate(0));
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

/**
 * The tax rate for the state being shipped to, looked up again when the rate held here was not looked up for it.
 *
 * Checkout charges the rate it is given, and the shipping step does not wait out a slow lookup, so the checkout and
 * each payment ask here rather than trusting the held rate. A lookup that fails, or that has not answered within
 * {@link TAX_RATE_LOOKUP_WAIT_MS}, answers the held rate, as a failed lookup always has; a late answer is still held
 * once it arrives. It never rejects, because the wallet handlers await it before their own try.
 *
 * @param {string|null} state - The shipping state's abbreviation.
 * @returns {Promise<number>}
 */
export const resolveTaxRate = (state) => {
	return async (dispatch, getState) => {
		const { taxRate, taxRateState } = { ...getState().cartState };
		const heldTaxRate = taxRate ?? 0;
		if (isStringEmpty(state) || state === taxRateState) {
			return heldTaxRate;
		}

		const lookup = lookUpTaxRate(state).then((resolvedTaxRate) => {
			if (getState().cartState.shippingAddress?.state === state) {
				dispatch(updateTaxRate(resolvedTaxRate, state));
			}

			return resolvedTaxRate;
		}).catch((error) => {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		});

		let waitTimer = null;
		const timedOut = new Promise((resolve) => { waitTimer = setTimeout(() => resolve(null), TAX_RATE_LOOKUP_WAIT_MS); });
		const resolvedTaxRate = await Promise.race([lookup, timedOut]);
		clearTimeout(waitTimer);

		return isNotNullOrUndefined(resolvedTaxRate) ? resolvedTaxRate : heldTaxRate;
	}
};

/**
 * Whether charging at `taxRate` would charge a total other than the one the customer was shown.
 *
 * A payment that resolved a rate the shown total was not taxed at recalculates the cart at the rate the cart now holds -
 * the one `resolveTaxRate` has just applied - and compares the totals, so a cart the rate does not change, such as a
 * tax-exempt one, carries on rather than being stopped. It never rejects: a cart that cannot be recalculated answers
 * `false`, because the wallet handlers await it before their own try.
 *
 * @param {number} taxRate - The rate the payment will charge.
 * @param {object|null} shownCartSummary - The cart summary the customer was shown.
 * @returns {Promise<boolean>}
 */
export const isCartTotalChangedByTaxRate = (taxRate, shownCartSummary) => {
	return async (dispatch) => {
		if (isNumberNotEmpty(shownCartSummary?.taxRate) && taxRate === shownCartSummary.taxRate) {
			return false;
		}

		const settledCartSummary = await dispatch(calculateCartSummary());
		return isObjectNotEmpty(settledCartSummary) && Math.round(settledCartSummary.total * 100) !== Math.round((shownCartSummary?.total ?? 0) * 100);
	}
};

export const updateBillingAddress = (billingAddress) => {
	return async (dispatch) => {
		try {
			if (isObjectNotEmpty(billingAddress)) {
				dispatch({ type: 'UPDATE_BILLING_ADDRESS', payload: { ...billingAddress } });
			} else {
				dispatch({ type: 'UPDATE_BILLING_ADDRESS', payload: null });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const refreshCartTotalQuantity = () => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			const updatedTotalQuantity = getTotalQuantity(cartItems);

			dispatch({ type: 'UPDATE_TOTAL_QUANTITY', payload: updatedTotalQuantity });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const addSingleItemToCart = (product, bundleCount, variant) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isObjectNotEmpty(product) && isStringNotEmpty(product.id)) {
				if (isFoundInCart(cartState, product.id)) {
					const cartItem = cartItems.find((item) => item.id === product.id);

					dispatch({ type: 'ADD_SINGLE_ITEM_TO_CART', payload: { ...cartItem, quantity: 1, bundleCount, variantId: variant?.id ?? null, variantSku: variant?.sku ?? null } });
				} else {
					dispatch({ type: 'ADD_SINGLE_ITEM_TO_CART', payload: new CartItem({ id: product?.id, sku: product?.sku, quantity: 1, bundleCount, variantId: variant?.id ?? null, variantSku: variant?.sku ?? null }).toDto() });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const addOrUpdateSingleItemToCart = (product, bundleCount, variant) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isObjectNotEmpty(product) && isStringNotEmpty(product.id)) {
				if (isFoundInCart(cartState, product.id)) {
					const cartItem = cartItems.find((item) => item.id === product.id);

					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: 1, bundleCount, variantId: variant?.id ?? null, variantSku: variant?.sku ?? null } });
				} else {
					dispatch({ type: 'ADD_TO_CART', payload: new CartItem({ id: product.id, sku: product?.sku, quantity: 1, bundleCount, variantId: variant?.id ?? null, variantSku: variant?.sku ?? null }).toDto() });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const addToCart = (product, quantity) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isObjectNotEmpty(product) && isStringNotEmpty(product.id)) {
				if (isFoundInCart(cartState, product.id)) {
					const cartItem = cartItems.find((item) => item.id === product.id);

					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: cartItem.quantity + quantity, bundleCount: 1, variantId: null, variant: null } });
				} else {
					dispatch({ type: 'ADD_TO_CART', payload: new CartItem({ id: product.id, sku: product?.sku, quantity: quantity, bundleCount: 1, variantId: null, variant: null }).toDto() });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const increaseQuantity = (id) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isStringNotEmpty(id) && isFoundInCart(cartState, id)) {
				const cartItem = cartItems.find((item) => item.id === id);
				dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: cartItem.quantity + 1 } });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const decreaseQuantity = (id) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isStringNotEmpty(id) && isFoundInCart(cartState, id)) {
				const cartItem = cartItems.find((item) => item.id === id);

				if (cartItem.quantity > 1) {
					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: cartItem.quantity - 1 } });
				} else {
					dispatch({ type: 'REMOVE_CART_ITEM', payload: id });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const changeBundleCount = (id, newBundleCount) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isStringNotEmpty(id) && isFoundInCart(cartState, id) && !isNaN(newBundleCount)) {
				const cartItem = cartItems.find((item) => item.id === id);
				// console.log('newBundleCount', newBundleCount);
				if (newBundleCount >= 1) {
					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, bundleCount: newBundleCount } });
				} else if (newBundleCount < 1) {
					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, bundleCount: 1 } });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const changeQuantity = (id, newQuantity) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isStringNotEmpty(id) && isFoundInCart(cartState, id) && !isNaN(newQuantity)) {
				const cartItem = cartItems.find((item) => item.id === id);

				if (newQuantity >= 1) {
					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: newQuantity } });
				} else if (newQuantity < 1) {
					dispatch({ type: 'UPDATE_CART_ITEM', payload: { ...cartItem, quantity: 1 } });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const removeFromCart = (id) => {
	return async (dispatch) => {
		try {
			if (isStringNotEmpty(id)) {
				dispatch({ type: 'REMOVE_CART_ITEM', payload: id });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const removeFromCartBySku = (productSku) => {
	return async (dispatch, getState) => {
		try {
			const state = getState();
			const cartState = { ...state.cartState };
			const { cartItems } = cartState;

			if (isStringNotEmpty(productSku)) {
				if (isFoundInCartBySku(cartState, productSku)) {
					const cartItem = cartItems.find((item) => item?.sku === productSku);

					dispatch({ type: 'REMOVE_CART_ITEM', payload: cartItem?.id });
				}
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const loadCartFromLead = (cartItems = []) => {
	return async (dispatch) => {
		try {
			if (isListNotEmpty(cartItems)) {
				// console.log('cart from lead', cartItems);
				dispatch({ type: 'UPDATE_ALL_CART_ITEMS', payload: cartItems });
			} else {
				dispatch({ type: 'UPDATE_ALL_CART_ITEMS', payload: [] });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const emptyCartItems = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'UPDATE_ALL_CART_ITEMS', payload: [] });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const clearCart = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'RESET_CART', payload: { ...initialState } });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const openCart = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'OPEN_CART', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const closeCart = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'CLOSE_CART', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const useCart = () => {
	const dispatch = useDispatch();

	const cartItems = useSelector((appState) => appState.cartState.cartItems);

	const isFoundInCartFunction = useCallback((cartItemId) => {
		return cartItems?.some((item) => item.id === cartItemId) || false;
	}, [cartItems]);

	const isFoundInCartBySkuFunction = useCallback((productSku) => {
		return cartItems?.some((item) => item?.sku === productSku) || false;
	}, [cartItems]);

	const isFoundInCartBySkuAndVariantSkuFunction = useCallback((productSku, variantSku) => {
		return cartItems?.some((item) => item?.sku === productSku && item?.variantSku === variantSku) || false;
	}, [cartItems]);

	const isCartEmpty = useCallback(() => {
		return cartItems.length === 0;
	}, [cartItems]);

	return {
		CartState: useSelector((appState) => appState.cartState),
		initialState: initialState,

		cartItems: useSelector((appState) => appState.cartState.cartItems),
		cartSummary: useSelector((appState) => appState.cartState.cartSummary),
		coupon: useSelector((appState) => appState.cartState.coupon),
		couponApplied: useSelector((appState) => appState.cartState.couponApplied),
		couponCodeApplied: useSelector((appState) => appState.cartState.couponCodeApplied),

		paymentMethod: useSelector((appState) => appState.cartState.paymentMethod),
		shippingMethod: useSelector((appState) => appState.cartState.shippingMethod),
		shippingAddress: useSelector((appState) => appState.cartState.shippingAddress),
		billingAddress: useSelector((appState) => appState.cartState.billingAddress),
		taxRate: useSelector((appState) => appState.cartState.taxRate),
		taxRateState: useSelector((appState) => appState.cartState.taxRateState),
		totalQuantity: useSelector((appState) => appState.cartState.totalQuantity),
		isCartOpen: useSelector((appState) => appState.cartState.isCartOpen),

		discountLines: useSelector((appState) => appState.cartState.cartSummary.discountLines),
		monthlyDiscountLines: useSelector((appState) => appState.cartState.cartSummary.monthlyDiscountLines),
		bundleDiscountLines: useSelector((appState) => appState.cartState.cartSummary.bundleDiscountLines),
		shippingLines: useSelector((appState) => appState.cartState.cartSummary.shippingLines),
		feeLines: useSelector((appState) => appState.cartState.cartSummary.feeLines),

		discountTotal: useSelector((appState) => appState.cartState.cartSummary.discountTotal),
		discountTax: useSelector((appState) => appState.cartState.cartSummary.discountTax),

		monthlyDiscountTotal: useSelector((appState) => appState.cartState.cartSummary.monthlyDiscountTotal),
		monthlyDiscountTax: useSelector((appState) => appState.cartState.cartSummary.monthlyDiscountTax),

		bundleDiscountTotal: useSelector((appState) => appState.cartState.cartSummary.bundleDiscountTotal),
		bundleDiscountTax: useSelector((appState) => appState.cartState.cartSummary.bundleDiscountTax),

		shippingTotal: useSelector((appState) => appState.cartState.cartSummary.shippingTotal),
		shippingTax: useSelector((appState) => appState.cartState.cartSummary.shippingTax),

		feeTotal: useSelector((appState) => appState.cartState.cartSummary.feeTotal),
		feeTax: useSelector((appState) => appState.cartState.cartSummary.feeTax),

		cartTotal: useSelector((appState) => appState.cartState.cartSummary.cartTotal),
		cartTax: useSelector((appState) => appState.cartState.cartSummary.cartTax),

		subtotal: useSelector((appState) => appState.cartState.cartSummary.subtotal),
		tax: useSelector((appState) => appState.cartState.cartSummary.tax),
		total: useSelector((appState) => appState.cartState.cartSummary.total),

		monthlySubtotal: useSelector((appState) => appState.cartState.cartSummary.monthlySubtotal),
		monthlyTax: useSelector((appState) => appState.cartState.cartSummary.monthlyTax),
		monthlyTotal: useSelector((appState) => appState.cartState.cartSummary.monthlyTotal),

		isCartLoading: useSelector((appState) => appState.cartState.isLoading),
		cartError: useSelector((appState) => appState.cartState.error),

		calculateCartSummary: () => dispatch(calculateCartSummary()),

		updateTaxRate: (taxRate, taxRateState) => dispatch(updateTaxRate(taxRate, taxRateState)),
		resolveTaxRate: (state) => dispatch(resolveTaxRate(state)),
		isCartTotalChangedByTaxRate: (taxRate, shownCartSummary) => dispatch(isCartTotalChangedByTaxRate(taxRate, shownCartSummary)),

		updateCouponCode: (couponCode, userId, email) => dispatch(updateCouponCode(couponCode, userId, email)),
		resetCouponCode: () => dispatch(resetCouponCode()),
		setCouponApplied: (coupon) => dispatch(setCouponApplied(coupon)),
		setCouponCodeApplied: (couponCode) => dispatch(setCouponCodeApplied(couponCode)),

		updatePaymentMethod: (paymentMethod) => dispatch(updatePaymentMethod(paymentMethod)),
		updateShippingMethod: (shippingMethod) => dispatch(updateShippingMethod(shippingMethod)),
		updateShippingAddress: (shippingAddress) => dispatch(updateShippingAddress(shippingAddress)),
		updateBillingAddress: (billingAddress) => dispatch(updateBillingAddress(billingAddress)),

		refreshCartTotalQuantity: () => dispatch(refreshCartTotalQuantity()),
		increaseQuantity: (cartItemId) => dispatch(increaseQuantity(cartItemId)),
		decreaseQuantity: (cartItemId) => dispatch(decreaseQuantity(cartItemId)),
		changeQuantity: (cartItemId, newQuantity) => dispatch(changeQuantity(cartItemId, newQuantity)),
		removeFromCart: (cartItemId) => dispatch(removeFromCart(cartItemId)),
		removeFromCartBySku: (productSku) => dispatch(removeFromCartBySku(productSku)),

		calculateShippingMethodCost: (shippingMethod, cartItems, taxRate, products, variants) => dispatch(calculateShippingMethodCost(shippingMethod, cartItems, taxRate, products, variants)),
		getCartItemTaxTotal: (cartItem, taxRate, products, variants) => dispatch(getCartItemTaxTotal(cartItem, taxRate, products, variants)),

		clearCart: () => dispatch(clearCart()),
		emptyCartItems: () => dispatch(emptyCartItems()),

		addSingleItemToCart: (product, bundleCount, variant) => dispatch(addSingleItemToCart(product, bundleCount, variant)),
		addOrUpdateSingleItemToCart: (product, bundleCount, variant) => dispatch(addOrUpdateSingleItemToCart(product, bundleCount, variant)),
		changeBundleCount: (cartItemId, newBundleCount) => dispatch(changeBundleCount(cartItemId, newBundleCount)),

		isFoundInCart: isFoundInCartFunction,
		isFoundInCartBySku: isFoundInCartBySkuFunction,
		isFoundInCartBySkuAndVariantSku: isFoundInCartBySkuAndVariantSkuFunction,
		isCartEmpty: isCartEmpty,

		loadCartFromLead: (cartItems) => dispatch(loadCartFromLead(cartItems)),

		addToCart: (product, quantity) => dispatch(addToCart(product, quantity)),
		openCart: () => dispatch(openCart()),
		closeCart: () => dispatch(closeCart())
	};
};

export default cartReducer;